import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Calendar, Users, MessageCircle, Mail, Phone, Clock, ExternalLink, FileText, Download } from 'lucide-react';
import { StepCard, InfoCard, ExampleCard } from './SectionComponents';

const MarketingInterviewSupportSection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {

  const supportOptions = [
    {
      icon: MessageCircle,
      title: "Mock interviews & feedback",
      description: "Practice your pitch and get feedback from experienced professionals",
      bookingInfo: "Book 30-min sessions through Calendly",
      calendlyLink: "https://calendly.com/d/cw8r-882-477/jumpstart-marketing-first-interview-prep-call",
      type: "success"
    },
    {
      icon: Users,
      title: "1:1 coaching sessions",
      description: "Personalized guidance on interview preparation and strategy",
      bookingInfo: "Available for candidates struggling with interviews",
      type: "tip"
    },
    {
      icon: Mail,
      title: "Application review",
      description: "Get feedback on your applications and profile",
      bookingInfo: "Email info@jumpstart-uk.com for support",
      type: "warning"
    }
  ];

  const preparationTips = [
    {
      title: "Practice your elevator pitch",
      description: "Record yourself and get feedback from others. Aim for 45-60 seconds that flows naturally."
    },
    {
      title: "Research the company thoroughly",
      description: "Know their product, team, recent news, and competitors. Come with specific questions and ideas."
    },
    {
      title: "Prepare STAR stories",
      description: "Have 3-5 concrete examples ready that demonstrate key marketing skills and achievements."
    },
    {
      title: "Plan your questions",
      description: "Prepare thoughtful, research-based questions that show your genuine interest and understanding."
    },
    {
      title: "Test your tech setup",
      description: "Ensure your camera, microphone, and internet connection work well for video calls."
    }
  ];

  const resourceLinks = [
    {
      text: "Book Mock Interview",
      url: "https://calendly.com/d/cw8r-882-477/jumpstart-marketing-first-interview-prep-call",
      icon: Calendar,
      className: "bg-[#02B491] text-white hover:bg-emerald-600"
    },
    {
      text: "Email for Support",
      url: "mailto:info@jumpstart-uk.com",
      icon: Mail,
      className: "bg-blue-500 text-white hover:bg-blue-600"
    }
  ];

  const coachingTopics = [
    "Structuring compelling answers using the STAR method",
    "Handling tough questions about career gaps or changes",
    "Demonstrating marketing skills through specific examples",
    "Showing enthusiasm and cultural fit",
    "Negotiating salary and offers",
    "Building confidence for interviews"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Interview support & coaching
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Get personalized support to ace your marketing interviews. From mock interviews to 1:1 coaching, we're here to help you succeed.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="🚀 Available support options">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                We offer various forms of interview support to help you prepare and perform your best:
              </p>
              
              <div className="grid gap-8 md:grid-cols-1">
                {supportOptions.map((option, index) => (
                  <InfoCard
                    key={index}
                    icon={option.icon}
                    title={option.title}
                    description={`${option.description}. ${option.bookingInfo}`}
                    type={option.type}
                  />
                ))}
              </div>

              <ExampleCard
                title="Quick Access Links"
                links={resourceLinks}
              />
            </div>
          </StepCard>

          <StepCard title="📋 Interview preparation checklist">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Use this checklist to ensure you're fully prepared for your marketing interviews:
              </p>
              
              <div className="space-y-6">
                {preparationTips.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{tip.title}</h4>
                      <p className="text-slate-600 leading-relaxed">{tip.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="🎯 What we cover in coaching sessions">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Our 1:1 coaching sessions are tailored to your specific needs and can cover:
              </p>
              
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="grid gap-4 md:grid-cols-2">
                  {coachingTopics.map((topic, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-[#02B491] rounded-full mt-2"></div>
                      <p className="text-slate-700 leading-relaxed">{topic}</p>
                    </div>
                  ))}
                </div>
              </div>

              <InfoCard
                icon={Clock}
                title="Session Duration & Booking"
                description="Sessions are typically 30-45 minutes and can be booked through our Calendly link. We recommend booking at least 24 hours before your interview for best availability."
                type="tip"
              />
            </div>
          </StepCard>

          <StepCard title="📞 How to get support">
            <div className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Book Mock Interview</h3>
                      <p className="text-slate-700 leading-relaxed">Schedule a practice session with our experienced coaches</p>
                    </div>
                  </div>
                  <a
                    href="https://calendly.com/d/cw8r-882-477/jumpstart-marketing-first-interview-prep-call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-[#02B491] text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Now
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Email Support</h3>
                      <p className="text-slate-700 leading-relaxed">Get help with applications, profile reviews, and general guidance</p>
                    </div>
                  </div>
                  <a
                    href="mailto:info@jumpstart-uk.com"
                    className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Us
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </a>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <p className="text-amber-800 text-sm font-medium">
                  💡 <strong>Best practice:</strong> Book support sessions as early as possible. We get busier closer to application deadlines and interview seasons.
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="Startup Jargon Buster: Essential Reading Before Your Interview">
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              Startups use their own language (what's PMF? MVP?) and they expect you to understand it.
            </p>
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              This cheat sheet explains 100+ key terms across business, marketing, finance, tech, and AI, with real examples and short notes on how they relate to your interviews.
            </p>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              If you're preparing for a startup role, make time to read it. You don't want to walk into an interview missing the basics everyone else knows.
            </p>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#02B491]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-8 h-8 text-[#02B491]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Startup Jargon Buster Guide</h4>
                    <p className="text-slate-600">PDF • Essential startup terminology explained</p>
                  </div>
                </div>
                <a
                  href="/Startup Jargon Buster 2025.11 .pdf"
                  download
                  className="inline-flex items-center px-8 py-4 bg-[#02B491] text-white rounded-xl font-semibold hover:bg-emerald-600 transition-all duration-300 shadow-lg shadow-emerald-500/20"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </a>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingInterviewSupportSection; 